// The only static import this module takes. `trainLayout` is a dependency-free leaf of pure
// constants, so it cannot undo the reason every model here is loaded dynamically — and these two
// constants are the contract for which component is a right-angle drive, which the layout and the
// registry have to answer the same way. See resolveRegistryKey.
import { ELBOW_ORIENTATION_PROPERTY, ELBOW_ORIENTATION_VALUE } from '../core/trainLayout';

const modelCache = new Map();
const modelCachePromises = new Map();
const slotDefsCache = new Map();
const slotDefsCachePromises = new Map();

/** Coerces a caller-supplied key to the canonical lower-case registry form. */
function normalizeModelKey(key) {
  return String(key || '').toLowerCase();
}

/**
 * One descriptor per model. This is the single source of truth: the subtype ladder, footprints,
 * shaft heights, the loader table, REAL_MODEL_KEYS and the activity component-type map are all
 * derived from it, so adding a component type is one entry here plus one model file.
 *
 * The descriptors live in THIS module rather than one per model file because each model file
 * imports three.js, so a `DESCRIPTOR` export per file would have to be imported eagerly to
 * resolve a key. `load()` keeps the geometry lazy while the metadata stays static.
 *
 * Order matters and is preserved: `subtypes` (exact) is tested for every descriptor before any
 * `subtypePatterns` (substring), so `axial_pump` wins over the generic `pump` pattern regardless
 * of array order. registryResolution.test.js pins all of it.
 *
 * `activityComponentTypes` must list the platform's umbrella component_type (`pump`, `turbine`,
 * `electrical`, `tank`, `valve`, `steam_trap`) alongside the subtype: the activities API reports
 * ComponentType.component_type and never the subtype, so an entry missing it never matches and
 * its activity surfaces on the machine-level strip instead of on the body.
 */
const DESCRIPTORS = [
  {
    key: 'ac_induction_motor',
    subtypes: ['ac_induction_motor', 'ac_synchronous_motor'],
    footprint: { x: 11.5, y: 8, z: 7 },
    activityComponentTypes: ['motor', 'turbine'],
    load: async () => (await import('./acInductionMotorModel')).createAcInductionMotorModel,
  },
  {
    key: 'dc_motor',
    subtypes: ['dc_motor'],
    footprint: { x: 10, y: 8, z: 6.5 },
    activityComponentTypes: ['motor'],
    load: async () => (await import('./dcMotorModel')).createDcMotorModel,
  },
  {
    key: 'diesel_engine',
    subtypes: ['diesel_engine'],
    footprint: { x: 13.7, y: 8.6, z: 6.1 },
    activityComponentTypes: ['engine', 'diesel_engine'],
    load: async () => (await import('./dieselEngineModel')).createDieselEngineModel,
  },
  {
    key: 'gas_turbine',
    subtypes: ['gas_turbine'],
    footprint: { x: 19.5, y: 9.5, z: 7.1 },
    activityComponentTypes: ['gas_turbine', 'turbine'],
    load: async () => (await import('./gasTurbineModel')).createGasTurbineModel,
  },
  {
    key: 'hydro_turbine',
    subtypes: ['hydro_turbine'],
    footprint: { x: 13, y: 8, z: 8.5 },
    activityComponentTypes: ['hydro_turbine', 'turbine'],
    load: async () => (await import('./hydroTurbineModel')).createHydroTurbineModel,
  },
  {
    key: 'steam_turbine',
    subtypes: ['steam_turbine'],
    footprint: { x: 16.5, y: 9.7, z: 8.1 },
    activityComponentTypes: ['steam_turbine', 'turbine'],
    load: async () => (await import('./steamTurbineModel')).createSteamTurbineModel,
  },
  {
    key: 'wind_turbine',
    subtypes: ['wind_turbine'],
    footprint: { x: 12.5, y: 27.5, z: 18.5 },
    activityComponentTypes: ['wind_turbine', 'turbine'],
    load: async () => (await import('./windTurbineModel')).createWindTurbineModel,
  },
  {
    key: 'gearbox',
    subtypes: ['gearbox'],
    footprint: { x: 11, y: 6, z: 5.5 },
    shaftAxisY: 0.3,
    activityComponentTypes: ['gearbox', 'multi_stage_gearbox'],
    load: async () => (await import('./gearboxModel')).createGearboxModel,
  },
  /*
   * The five remaining electrical bodies; `transformer` sits just below with its own descriptor.
   *
   * All EXACT subtypes, deliberately no pattern: `switch_gear` and `circuit_breaker` are both
   * switching apparatus and a pattern on either word would capture the other's components, and
   * `substation` contains a breaker and a transformer without being either.
   */
  {
    key: 'circuit_breaker',
    subtypes: ['circuit_breaker'],
    footprint: { x: 13.5, y: 9, z: 3 },
    activityComponentTypes: ['electrical', 'circuit_breaker'],
    load: async () => (await import('./circuitBreakerModel')).createCircuitBreakerModel,
  },
  {
    key: 'electrical_panel',
    subtypes: ['electrical_panel'],
    footprint: { x: 12.5, y: 7.5, z: 3.5 },
    activityComponentTypes: ['electrical', 'electrical_panel'],
    load: async () => (await import('./electricalPanelModel')).createElectricalPanelModel,
  },
  {
    key: 'switch_gear',
    subtypes: ['switch_gear'],
    footprint: { x: 11.5, y: 8.5, z: 4 },
    activityComponentTypes: ['electrical', 'switch_gear'],
    load: async () => (await import('./switchGearModel')).createSwitchGearModel,
  },
  {
    key: 'surge_arrester',
    subtypes: ['surge_arrester'],
    footprint: { x: 2.5, y: 8.5, z: 2.5 },
    activityComponentTypes: ['electrical', 'surge_arrester'],
    load: async () => (await import('./surgeArresterModel')).createSurgeArresterModel,
  },
  {
    key: 'substation',
    subtypes: ['substation'],
    footprint: { x: 30, y: 12.5, z: 13 },
    activityComponentTypes: ['electrical', 'substation'],
    load: async () => (await import('./substationModel')).createSubstationModel,
  },
  {
    key: 'transformer',
    subtypePatterns: ['transformer'],
    footprint: { x: 14.5, y: 15.5, z: 11.5 },
    activityComponentTypes: ['transformer', 'electrical'],
    load: async () => (await import('./transformerModel')).createTransformerModel,
  },
  /*
   * The nine tank bodies.
   *
   * All EXACT subtypes, with deliberately no `subtypePatterns: ['tank']`: a `tank` pattern would
   * be matched by all nine, and since pattern matching runs in array order it would hand
   * whichever tank is declared first every other tank's components.
   *
   * None is flagged `verticalBody`. That flag stands a SHAFTED body on end so its shaft points
   * up; a tank has no shaft, and the vertical ones are already authored standing up.
   */
  {
    key: 'vertical_cylinder_tank',
    subtypes: ['vertical_cylinder_tank'],
    footprint: { x: 9, y: 14.5, z: 10 },
    activityComponentTypes: ['tank', 'vertical_cylinder_tank'],
    load: async () => (await import('./verticalCylinderTankModel')).createVerticalCylinderTankModel,
  },
  {
    key: 'vertical_capsule_tank',
    subtypes: ['vertical_capsule_tank'],
    footprint: { x: 8, y: 15, z: 9 },
    activityComponentTypes: ['tank', 'vertical_capsule_tank'],
    load: async () => (await import('./verticalCapsuleTankModel')).createVerticalCapsuleTankModel,
  },
  {
    key: 'cone_bottom_tank',
    subtypes: ['cone_bottom_tank'],
    footprint: { x: 9, y: 14, z: 10 },
    activityComponentTypes: ['tank', 'cone_bottom_tank'],
    load: async () => (await import('./coneBottomTankModel')).createConeBottomTankModel,
  },
  {
    key: 'frustum_tank',
    subtypes: ['frustum_tank'],
    footprint: { x: 9.5, y: 12, z: 9.5 },
    activityComponentTypes: ['tank', 'frustum_tank'],
    load: async () => (await import('./frustumTankModel')).createFrustumTankModel,
  },
  {
    key: 'horizontal_cylinder_tank',
    subtypes: ['horizontal_cylinder_tank'],
    footprint: { x: 13, y: 9.5, z: 8 },
    activityComponentTypes: ['tank', 'horizontal_cylinder_tank'],
    load: async () => (await import('./horizontalCylinderTankModel')).createHorizontalCylinderTankModel,
  },
  {
    key: 'horizontal_capsule_tank',
    subtypes: ['horizontal_capsule_tank'],
    footprint: { x: 15, y: 9, z: 7.5 },
    activityComponentTypes: ['tank', 'horizontal_capsule_tank'],
    load: async () => (await import('./horizontalCapsuleTankModel')).createHorizontalCapsuleTankModel,
  },
  {
    key: 'horizontal_oval_tank',
    subtypes: ['horizontal_oval_tank'],
    footprint: { x: 12.5, y: 7, z: 8.5 },
    activityComponentTypes: ['tank', 'horizontal_oval_tank'],
    load: async () => (await import('./horizontalOvalTankModel')).createHorizontalOvalTankModel,
  },
  {
    key: 'vertical_oval_tank',
    subtypes: ['vertical_oval_tank'],
    footprint: { x: 9, y: 11.5, z: 7 },
    activityComponentTypes: ['tank', 'vertical_oval_tank'],
    load: async () => (await import('./verticalOvalTankModel')).createVerticalOvalTankModel,
  },
  {
    key: 'rectangular_tank',
    subtypes: ['rectangular_tank'],
    footprint: { x: 13.5, y: 7.5, z: 10 },
    activityComponentTypes: ['tank', 'rectangular_tank'],
    load: async () => (await import('./rectangularTankModel')).createRectangularTankModel,
  },
  /*
   * The six valve bodies, all typed `valve` on the platform side. Exact-only, with no `valve`
   * pattern: the six shapes have nothing in common that a generic stand-in could represent — one
   * has no operator at all — so a bare `valve` subtype falls through to the labelled box.
   */
  {
    key: 'gate_valve',
    subtypes: ['gate_valve'],
    footprint: { x: 7, y: 9, z: 3.5 },
    activityComponentTypes: ['valve', 'gate_valve'],
    load: async () => (await import('./gateValveModel')).createGateValveModel,
  },
  {
    key: 'globe_valve',
    subtypes: ['globe_valve'],
    footprint: { x: 7.5, y: 8, z: 3.5 },
    activityComponentTypes: ['valve', 'globe_valve'],
    load: async () => (await import('./globeValveModel')).createGlobeValveModel,
  },
  {
    key: 'diaphragm_valve',
    subtypes: ['diaphragm_valve'],
    footprint: { x: 8, y: 6.5, z: 3.5 },
    activityComponentTypes: ['valve', 'diaphragm_valve'],
    load: async () => (await import('./diaphragmValveModel')).createDiaphragmValveModel,
  },
  {
    key: 'disc_check_valve',
    subtypes: ['disc_check_valve'],
    footprint: { x: 8.5, y: 4, z: 3.5 },
    activityComponentTypes: ['valve', 'disc_check_valve'],
    load: async () => (await import('./discCheckValveModel')).createDiscCheckValveModel,
  },
  {
    key: 'pinch_valve',
    subtypes: ['pinch_valve'],
    footprint: { x: 6.5, y: 8, z: 3.5 },
    activityComponentTypes: ['valve', 'pinch_valve'],
    load: async () => (await import('./pinchValveModel')).createPinchValveModel,
  },
  {
    key: 'plug_valve',
    subtypes: ['plug_valve'],
    footprint: { x: 7, y: 5.5, z: 3.5 },
    activityComponentTypes: ['valve', 'plug_valve'],
    load: async () => (await import('./plugValveModel')).createPlugValveModel,
  },
  /*
   * The five steam traps, all typed `steam_trap` on the platform side. Exact-only, with no `trap`
   * pattern, for the same reason the six valves are: a float trap is a bolted vessel, a disc trap
   * is a fitting with a cap on it and a bucket trap stands up, so no generic stand-in fits.
   *
   * `steam_trap` is the umbrella subtype AND a subtype in its own right, with its own body. All
   * five are modelled from product photographs; the 2D set is used only for its embedded
   * sensor-pad anchors, which are placement data rather than shape (steam_trap.svg is byte-for-
   * byte mechanical_inverted_bucket.svg, which says the asset set reused a drawing).
   */
  {
    key: 'mechanical_float_trap',
    subtypes: ['mechanical_float_trap'],
    footprint: { x: 9.2, y: 11, z: 6.9 },
    activityComponentTypes: ['steam_trap', 'mechanical_float_trap'],
    load: async () => (await import('./mechanicalFloatTrapModel')).createMechanicalFloatTrapModel,
  },
  {
    key: 'mechanical_inverted_bucket',
    subtypes: ['mechanical_inverted_bucket'],
    footprint: { x: 7.4, y: 8.4, z: 5.2 },
    activityComponentTypes: ['steam_trap', 'mechanical_inverted_bucket'],
    load: async () => (await import('./mechanicalInvertedBucketModel'))
      .createMechanicalInvertedBucketModel,
  },
  {
    key: 'steam_trap',
    subtypes: ['steam_trap'],
    footprint: { x: 6.6, y: 4.5, z: 3.8 },
    activityComponentTypes: ['steam_trap'],
    load: async () => (await import('./steamTrapModel')).createSteamTrapModel,
  },
  {
    key: 'thermodynamic_disc_trap',
    subtypes: ['thermodynamic_disc_trap'],
    footprint: { x: 7, y: 6.1, z: 3 },
    activityComponentTypes: ['steam_trap', 'thermodynamic_disc_trap'],
    load: async () => (await import('./thermodynamicDiscTrapModel'))
      .createThermodynamicDiscTrapModel,
  },
  {
    key: 'thermostatic_bellow_type',
    subtypes: ['thermostatic_bellow_type'],
    footprint: { x: 7.1, y: 5, z: 3.8 },
    activityComponentTypes: ['steam_trap', 'thermostatic_bellow_type'],
    load: async () => (await import('./thermostaticBellowTypeModel'))
      .createThermostaticBellowTypeModel,
  },
  /*
   * The four "Non Rotating / Other" bodies — the platform's residual non-rotating group. All
   * EXACT subtypes and deliberately no patterns: `generic` must not become a substring rule, and
   * the labelled `fallback` body already handles component types the twin has never heard of.
   *
   * `pac` carries BOTH cases of its platform component_type: the enum spells it `PAC` while the
   * subtype is lower case, and activity lookups are done on the raw string.
   */
  {
    key: 'dehumidifier',
    subtypes: ['dehumidifier'],
    footprint: { x: 16.4, y: 7.8, z: 4.8 },
    activityComponentTypes: ['dehumidifier'],
    load: async () => (await import('./dehumidifierModel')).createDehumidifierModel,
  },
  {
    key: 'pac',
    subtypes: ['pac'],
    footprint: { x: 11.4, y: 8, z: 4 },
    activityComponentTypes: ['PAC', 'pac'],
    load: async () => (await import('./pacModel')).createPacModel,
  },
  {
    key: 'building_location',
    subtypes: ['building_location'],
    footprint: { x: 14.4, y: 9.6, z: 8.8 },
    activityComponentTypes: ['building_location'],
    load: async () => (await import('./buildingLocationModel')).createBuildingLocationModel,
  },
  {
    key: 'generic',
    subtypes: ['generic'],
    footprint: { x: 8, y: 6.2, z: 4.8 },
    activityComponentTypes: ['generic'],
    load: async () => (await import('./genericModel')).createGenericModel,
  },
  {
    key: 'axial_pump',
    subtypes: ['axial_pump'],
    footprint: { x: 9, y: 6.5, z: 5 },
    activityComponentTypes: ['axial_pump', 'pump'],
    load: async () => (await import('./axialPumpModel')).createAxialPumpModel,
  },
  {
    key: 'centrifugal_pump',
    subtypes: ['centrifugal_pump'],
    footprint: { x: 13, y: 9.5, z: 6.5 },
    activityComponentTypes: ['centrifugal_pump', 'pump'],
    load: async () => (await import('./centrifugalPumpModel')).createCentrifugalPumpModel,
  },
  {
    key: 'horizontal_single_shaft_centrifugal_pump',
    subtypes: ['horizontal_single_shaft_centrifugal_pump'],
    footprint: { x: 13.5, y: 8.5, z: 8.5 },
    activityComponentTypes: ['horizontal_single_shaft_centrifugal_pump', 'pump'],
    load: async () => (
      await import('./horizontalSingleShaftCentrifugalPumpModel')
    ).createHorizontalSingleShaftCentrifugalPumpModel,
  },
  {
    key: 'gear_pump',
    subtypes: ['gear_pump'],
    footprint: { x: 11, y: 6, z: 5 },
    activityComponentTypes: ['gear_pump', 'pump'],
    load: async () => (await import('./gearPumpModel')).createGearPumpModel,
  },
  {
    key: 'progressive_cavity_pump',
    subtypes: ['progressive_cavity_pump'],
    footprint: { x: 19, y: 6.1, z: 4.1 },
    activityComponentTypes: ['progressive_cavity_pump', 'pump'],
    load: async () => (
      await import('./progressiveCavityPumpModel')
    ).createProgressiveCavityPumpModel,
  },
  {
    key: 'piston_pump',
    subtypes: ['piston_pump', 'plunger_pump'],
    footprint: { x: 9, y: 10, z: 6 },
    activityComponentTypes: ['piston_pump', 'plunger_pump', 'pump'],
    load: async () => (await import('./pistonPumpModel')).createPistonPumpModel,
  },
  {
    key: 'single_shaft_multistage_pump',
    subtypes: ['single_shaft_multistage_pump'],
    footprint: { x: 18.5, y: 7, z: 5 },
    activityComponentTypes: ['single_shaft_multistage_pump', 'pump'],
    load: async () => (
      await import('./singleShaftMultistagePumpModel')
    ).createSingleShaftMultistagePumpModel,
  },
  {
    key: 'vertical_turbine_pump',
    subtypes: ['vertical_turbine_pump'],
    // A VTP's shaft is vertical in every installation there is, whatever orientation the machine
    // it belongs to is recorded as. See isVerticalBody below.
    verticalBody: true,
    footprint: { x: 25.9, y: 5.3, z: 6.3 },
    activityComponentTypes: ['vertical_turbine_pump', 'pump'],
    load: async () => (
      await import('./verticalTurbinePumpModel')
    ).createVerticalTurbinePumpModel,
  },
  {
    key: 'screw_pump',
    subtypes: ['screw_pump'],
    footprint: { x: 14.5, y: 6.5, z: 5.5 },
    activityComponentTypes: ['screw_pump', 'pump'],
    load: async () => (await import('./screwPumpModel')).createScrewPumpModel,
  },
  {
    key: 'belt_drive',
    subtypes: ['belt_drive'],
    footprint: { x: 2.75, y: 8.5, z: 6.25 },
    activityComponentTypes: ['belt_drive', 'transmission_drive'],
    load: async () => (await import('./beltDriveModel')).createBeltDriveModel,
  },
  {
    key: 'solid_coupling',
    subtypes: ['solid_coupling'],
    subtypePatterns: ['solid_coupling'],
    footprint: { x: 3.5, y: 3, z: 3 },
    activityComponentTypes: ['solid_coupling', 'coupling'],
    load: async () => (await import('./solidCouplingModel')).createSolidCouplingModel,
  },
  {
    key: 'pillow_bearing',
    subtypes: ['pillow_bearing'],
    footprint: { x: 4.5, y: 3.5, z: 4 },
    shaftAxisY: 0.05,
    activityComponentTypes: ['bearing'],
    load: async () => (await import('./pillowBearingModel')).createPillowBearingModel,
  },
  {
    key: 'mounted_bearing',
    subtypes: ['mounted_bearing'],
    footprint: { x: 4, y: 6, z: 6 },
    activityComponentTypes: ['mounted_bearing', 'bearing'],
    load: async () => (await import('./mountedBearingModel')).createMountedBearingModel,
  },
  {
    key: 'mill_bearing',
    subtypes: ['mill_bearing'],
    footprint: { x: 10, y: 9.5, z: 8.5 },
    activityComponentTypes: ['mill_bearing', 'bearing'],
    load: async () => (await import('./millBearingModel')).createMillBearingModel,
  },
  {
    key: 'multi_stage_gearbox',
    subtypes: ['multi_stage_gearbox'],
    footprint: { x: 13.5, y: 8, z: 8.5 },
    activityComponentTypes: ['multi_stage_gearbox', 'gearbox'],
    load: async () => (await import('./multiStageGearboxModel')).createMultiStageGearboxModel,
  },
  {
    key: 'multi_stage_gearbox_vertical',
    subtypes: [],
    footprint: { x: 6, y: 7.5, z: 5 },
    activityComponentTypes: ['multi_stage_gearbox', 'gearbox'],
    load: async () => (
      await import('./multiStageGearboxVerticalModel')
    ).createMultiStageGearboxVerticalModel,
  },
  {
    key: 'chain_drive',
    subtypes: ['chain_drive'],
    footprint: { x: 3, y: 9.5, z: 6 },
    activityComponentTypes: ['chain_drive', 'transmission_drive'],
    load: async () => (await import('./chainDriveModel')).createChainDriveModel,
  },
  {
    key: 'pulley',
    subtypes: ['pulley'],
    footprint: { x: 3.5, y: 6, z: 6 },
    activityComponentTypes: ['pulley', 'transmission_drive'],
    load: async () => (await import('./pulleyModel')).createPulleyModel,
  },
  {
    key: 'simple_shaft',
    subtypes: ['simple_shaft'],
    footprint: { x: 11.5, y: 2, z: 2 },
    activityComponentTypes: ['simple_shaft', 'shaft'],
    load: async () => (await import('./simpleShaftModel')).createSimpleShaftModel,
  },
  {
    key: 'flexible_coupling',
    subtypes: ['flexible_coupling'],
    footprint: { x: 5, y: 4.5, z: 4.5 },
    activityComponentTypes: ['flexible_coupling', 'coupling'],
    load: async () => (await import('./flexibleCouplingModel')).createFlexibleCouplingModel,
  },
  {
    key: 'jaw_coupling',
    subtypes: ['jaw_coupling'],
    footprint: { x: 4.5, y: 3.5, z: 3.5 },
    activityComponentTypes: ['jaw_coupling', 'coupling'],
    load: async () => (await import('./jawCouplingModel')).createJawCouplingModel,
  },
  {
    key: 'fluid_coupling',
    subtypes: ['fluid_coupling'],
    footprint: { x: 6, y: 6.5, z: 6.5 },
    activityComponentTypes: ['fluid_coupling', 'coupling'],
    load: async () => (await import('./fluidCouplingModel')).createFluidCouplingModel,
  },
  {
    key: 'magnetic_coupling',
    subtypes: ['magnetic_coupling'],
    footprint: { x: 7.5, y: 4.5, z: 4.5 },
    activityComponentTypes: ['magnetic_coupling', 'coupling'],
    load: async () => (await import('./magneticCouplingModel')).createMagneticCouplingModel,
  },
  {
    key: 'centrifugal_fan',
    subtypes: ['centrifugal_fan'],
    footprint: { x: 8.1, y: 7.5, z: 7.3 },
    activityComponentTypes: ['centrifugal_fan'],
    load: async () => (await import('./centrifugalFanModel')).createCentrifugalFanModel,
  },
  {
    key: 'axial_fan',
    subtypes: ['axial_fan'],
    footprint: { x: 6.7, y: 7.5, z: 7.4 },
    activityComponentTypes: ['axial_fan'],
    load: async () => (await import('./axialFanModel')).createAxialFanModel,
  },
  {
    key: 'centrifugal_compressor',
    subtypes: ['centrifugal_compressor'],
    footprint: { x: 13.5, y: 8.5, z: 7 },
    activityComponentTypes: ['centrifugal_compressor', 'compressor'],
    load: async () => (
      await import('./centrifugalCompressorModel')
    ).createCentrifugalCompressorModel,
  },
  {
    key: 'axial_compressor',
    subtypes: ['axial_compressor'],
    footprint: { x: 14, y: 8.25, z: 6 },
    activityComponentTypes: ['axial_compressor', 'compressor'],
    load: async () => (await import('./axialCompressorModel')).createAxialCompressorModel,
  },
  {
    key: 'reciprocating_compressor',
    subtypes: ['reciprocating_compressor'],
    footprint: { x: 11.7, y: 5.2, z: 12 },
    activityComponentTypes: ['reciprocating_compressor', 'compressor'],
    load: async () => (
      await import('./reciprocatingCompressorModel')
    ).createReciprocatingCompressorModel,
  },
  {
    key: 'screw_compressor',
    subtypes: ['screw_compressor'],
    footprint: { x: 14.6, y: 7.3, z: 7 },
    activityComponentTypes: ['screw_compressor', 'compressor'],
    load: async () => (await import('./screwCompressorModel')).createScrewCompressorModel,
  },
  {
    key: 'two_stage_screw_compressor',
    subtypes: ['two_stage_screw_compressor'],
    footprint: { x: 13.4, y: 10.1, z: 8 },
    activityComponentTypes: ['two_stage_screw_compressor', 'compressor', 'screw_compressor'],
    load: async () => (
      await import('./twoStageScrewCompressorModel')
    ).createTwoStageScrewCompressorModel,
  },
  {
    key: 'agitator',
    subtypes: ['agitator'],
    // Deliberately NOT verticalBody. The agitator was flagged as one on the grounds that "the
    // 2D graphic draws it upright" — but the platform ships TWO graphics for it, and it is
    // `agitator_vertical.svg` that stands up. `agitator.svg`, the one a horizontal machine
    // renders, draws the shaft lying along the train with the seal and pedestal at the drive
    // end, which is exactly how this body is authored. Standing it up in a horizontal train is
    // what made the two views disagree, not what fixed it.
    //
    // A vertical MACHINE still stands it up: seatTrainComponents rotates every component by
    // rotation.z = π/2 when machine.orientation is 'vertical', which is the case
    // agitator_vertical.svg is drawn for. The flag is only for a body that must stand up
    // INSIDE a horizontal train, and this is not one.
    footprint: { x: 13.5, y: 5, z: 5 },
    activityComponentTypes: ['agitator'],
    load: async () => (await import('./agitatorModel')).createAgitatorModel,
  },
  {
    key: 'ball_mill',
    subtypes: ['ball_mill'],
    footprint: { x: 19.3, y: 10.1, z: 8.8 },
    activityComponentTypes: ['ball_mill', 'mill'],
    load: async () => (await import('./ballMillModel')).createBallMillModel,
  },
  {
    key: 'sag_mill',
    subtypes: ['sag_mill'],
    footprint: { x: 19.9, y: 14, z: 12.3 },
    activityComponentTypes: ['sag_mill', 'mill'],
    load: async () => (await import('./sagMillModel')).createSagMillModel,
  },
  {
    key: 'calendar_roll',
    subtypes: ['calendar_roll'],
    footprint: { x: 20.5, y: 5.5, z: 4.5 },
    activityComponentTypes: ['calendar_roll', 'roll'],
    load: async () => (await import('./calendarRollModel')).createCalendarRollModel,
  },
  {
    key: 'jaw_crusher',
    subtypes: ['jaw_crusher'],
    footprint: { x: 13, y: 12, z: 11 },
    activityComponentTypes: ['jaw_crusher', 'crusher'],
    load: async () => (await import('./jawCrusherModel')).createJawCrusherModel,
  },
  {
    key: 'conveyor',
    subtypes: ['conveyor'],
    footprint: { x: 22, y: 8.5, z: 7 },
    activityComponentTypes: ['conveyor'],
    load: async () => (await import('./conveyorModel')).createConveyorModel,
  },
  {
    key: 'conveyor_pulley',
    subtypes: ['conveyor_pulley'],
    footprint: { x: 9.5, y: 4.5, z: 4 },
    activityComponentTypes: ['conveyor_pulley'],
    load: async () => (await import('./conveyorPulleyModel')).createConveyorPulleyModel,
  },
  {
    key: 'pump',
    // The stand-in for the long tail of pump subtypes with no model of their own. The pumps
    // listed exactly above keep their own bodies.
    subtypePatterns: ['pump'],
    footprint: { x: 6.5, y: 4.5, z: 4.5 },
    activityComponentTypes: ['pump'],
    load: async () => (await import('./pumpModel')).createPumpModel,
  },
  {
    key: 'coupling',
    subtypePatterns: ['coupling'],
    footprint: { x: 2.5, y: 2.5, z: 2 },
    activityComponentTypes: ['coupling', 'transmission_drive'],
    load: async () => (await import('./couplingModel')).createCouplingModel,
  },
  {
    key: 'compressor',
    // The compressor body also stands in for the long tail of generic fan subtypes with no model
    // of their own. `centrifugal_fan`, `axial_fan` and `centrifugal_compressor` match exactly
    // above and keep their own bodies.
    subtypePatterns: ['compressor', 'fan'],
    footprint: { x: 5, y: 4.5, z: 9 },
    activityComponentTypes: ['compressor', 'screw_compressor', 'fan'],
    load: async () => (await import('./compressorModel')).createCompressorModel,
  },
];

const FALLBACK_DESCRIPTOR = {
  key: 'fallback',
  footprint: { x: 7, y: 3, z: 3 },
  activityComponentTypes: [],
  load: async () => (await import('./fallbackModel')).createFallbackModel,
};

const BY_KEY = {};
DESCRIPTORS.concat(FALLBACK_DESCRIPTOR).forEach((d) => { BY_KEY[d.key] = d; });

/**
 * Map an API component to a registry key. The `component_subtype` is very nearly the only input —
 * the twin persists nothing about which model a component uses.
 *
 * The ONE exception is the multi-stage gearbox, which is two different machines under one
 * subtype: a parallel-shaft unit whose output leaves horizontally, and a right-angle unit whose
 * output leaves straight DOWN. `properties.orientation` (labelled "Shaft orientation" in the
 * machine builder) decides which, and the 2D graphic branches on the same field. It is read
 * through the trainLayout constants on purpose: the SAME field decides whether the train bends
 * (`findElbowPivotIndex`), and the model and the layout must never disagree.
 *
 * Callers with only a subtype in hand — the palette, the drag ghost — pass no properties and get
 * the horizontal body, which is the correct default.
 */
export function resolveRegistryKey(component) {
  const st = normalizeModelKey(component && component.component_subtype);
  if (!st) return 'fallback';
  if (st === 'multi_stage_gearbox') {
    const properties = (component && component.properties) || null;
    if (properties && properties[ELBOW_ORIENTATION_PROPERTY] === ELBOW_ORIENTATION_VALUE) {
      return 'multi_stage_gearbox_vertical';
    }
  }
  // Exact matches across ALL descriptors first, so a specific subtype can never
  // be captured by another descriptor's substring pattern.
  for (let i = 0; i < DESCRIPTORS.length; i += 1) {
    const d = DESCRIPTORS[i];
    if (d.subtypes && d.subtypes.indexOf(st) !== -1) return d.key;
  }
  for (let i = 0; i < DESCRIPTORS.length; i += 1) {
    const d = DESCRIPTORS[i];
    if (d.subtypePatterns && d.subtypePatterns.some(pat => st.includes(pat))) return d.key;
  }
  return 'fallback';
}

/**
 * Bounding-box footprint (world units) — an upper bound on the model's actual extent, rounded up
 * to the next half unit.
 *
 * Feeds the drag-ghost / insertion placeholder size in SceneManager and the dimensions
 * ComponentPalette shows. Train layout does NOT use it — that measures the loaded model.
 * modelConventions.test.js measures every model against this table.
 */
export function getModelFootprint(key) {
  const d = BY_KEY[normalizeModelKey(key || 'fallback')];
  return (d || FALLBACK_DESCRIPTOR).footprint;
}

/**
 * Local Y of a model's shaft / rotational centerline, in the model's own coordinate space (i.e.
 * before ground-snap).
 *
 * Coupled components are spaced along X and snapped to the floor individually, which leaves
 * their shafts at different heights because each model has a different base-to-shaft distance.
 * SceneManager uses this to lift each component onto one continuous line. Most models are
 * authored centered on their shaft axis (y = 0); a couple sit slightly above center.
 */
export function getModelShaftAxisY(key) {
  const d = BY_KEY[normalizeModelKey(key || 'fallback')];
  return (d && d.shaftAxisY) || 0;
}

/**
 * Local Y of the shaft this model DRIVES, where that differs from the shaft that drives it.
 *
 * Almost every body is a single shaft line straight through, so this defaults to
 * `getModelShaftAxisY`. A parallel-shaft multi-stage gearbox is the exception: it takes its input
 * high and puts its output lower, so the run after it steps down — see `getModelShaftStepY`.
 * The right-angle body turns the run through 90° instead, which trainLayout.js handles.
 */
export function getModelOutputShaftAxisY(key) {
  const d = BY_KEY[normalizeModelKey(key || 'fallback')];
  if (d && Number.isFinite(d.outputShaftAxisY)) return d.outputShaftAxisY;
  return getModelShaftAxisY(key);
}

/**
 * How far the run downstream of this body drops, in the model's own space. 0 for everything
 * whose shaft passes straight through, which is nearly everything.
 */
export function getModelShaftStepY(key) {
  return getModelOutputShaftAxisY(key) - getModelShaftAxisY(key);
}

/**
 * Whether this body's shaft is vertical in the real world regardless of how the machine around
 * it is laid out.
 *
 * This is NOT `machine.orientation`, which is the machine-level case where the whole train stands
 * on end (handled in SceneManager.populateFromSceneData). A vertical turbine pump is vertical
 * even inside a horizontal train, because its column hangs down a wet pit. It is still authored
 * along +X like every other model — the conventions contract is about the FACTORY's frame — and
 * it is the SCENE that stands it up.
 *
 * Candidates to add here are bodies whose reference installation is vertical, not merely ones
 * that CAN be mounted vertically.
 */
export function isVerticalBody(key) {
  const d = BY_KEY[normalizeModelKey(key || 'fallback')];
  return !!(d && d.verticalBody);
}

const MODEL_LOADERS = {};
DESCRIPTORS.concat(FALLBACK_DESCRIPTOR).forEach((d) => { MODEL_LOADERS[d.key] = d.load; });

/**
 * Keys backed by real geometry (everything else renders the generic labelled box).
 * componentCatalog derives palette availability from this, so the two can't drift.
 */
export const REAL_MODEL_KEYS = new Set(DESCRIPTORS.map(d => d.key));

export function hasRealModel(key) {
  return REAL_MODEL_KEYS.has(normalizeModelKey(key));
}

/**
 * DB `component_type` (as the activity API reports it) -> the registry keys that represent it.
 * Derived from the descriptors rather than hand-maintained: the hand-written copy in
 * eventFilters.js had drifted and silently dropped machine-level activity on four bodies.
 */
export const ACTIVITY_TYPE_TO_REGISTRY_KEYS = (() => {
  const map = {};
  DESCRIPTORS.forEach((d) => {
    (d.activityComponentTypes || []).forEach((t) => {
      if (!map[t]) map[t] = [];
      map[t].push(d.key);
    });
  });
  // A fan-typed activity should reach every fan body — the two dedicated ones
  // and the compressor stand-in the remaining fan subtypes still resolve to.
  ['centrifugal_fan', 'axial_fan'].forEach((key) => {
    if (map.fan && map.fan.indexOf(key) === -1) map.fan.push(key);
  });
  return map;
})();

async function loadFactory(key) {
  const k = normalizeModelKey(key);
  return (MODEL_LOADERS[k] || MODEL_LOADERS.fallback)();
}

/**
 * Returns a cloned group for the given model registry key (cached by key). The Promise is cached
 * so concurrent callers share one in-flight load instead of each building their own prototype.
 */
export async function getModel(modelType, config = {}) {
  const key = normalizeModelKey(modelType || 'fallback');
  // Cached by registry key only. The fallback label is NOT part of the key — it is applied
  // per-instance below, so the cache stays bounded by the number of model types.
  if (!modelCachePromises.has(key)) {
    const pending = loadFactory(key).then((factory) => {
      const proto = factory();
      modelCache.set(key, proto);
      return proto;
    });
    // Evict on failure. A code-split chunk 404s whenever a user opens the tab from a stale page
    // across a deploy, and a memoised rejection makes the Retry button re-enter this function
    // and fail without ever re-attempting the import.
    pending.catch(() => {
      modelCachePromises.delete(key);
    });
    modelCachePromises.set(key, pending);
  }
  const proto = await modelCachePromises.get(key);
  const clone = proto.clone(true);
  if (key === 'fallback') {
    // Resolved from the module cache — loadFactory already imported it — so this
    // costs nothing and keeps fallbackModel out of the registry's own chunk.
    const { attachFallbackLabel } = await import('./fallbackModel');
    // Owned by this clone and tagged for the per-instance disposal sweep.
    attachFallbackLabel(clone, config.label || '?');
  }
  return clone;
}

/**
 * Frees every cached prototype. Only safe when no clone is live — SceneCanvas.dispose()
 * deliberately does NOT call this, since the cache is shared across every mounted scene.
 */
export function clearModelCache() {
  modelCache.forEach((group) => {
    group.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      // Material.dispose() does not reach .map, so a texture on a prototype would survive this.
      const tracked = obj.userData && obj.userData.disposables;
      if (Array.isArray(tracked)) {
        tracked.forEach(d => d && typeof d.dispose === 'function' && d.dispose());
      }
      if (obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => {
          if (!m) return;
          if (m.map && typeof m.map.dispose === 'function') m.map.dispose();
          m.dispose();
        });
      }
    });
  });
  modelCache.clear();
  modelCachePromises.clear();
  slotDefsCache.clear();
  slotDefsCachePromises.clear();
}

/**
 * Async accessor for a model's sensor slot definitions, authored alongside their model factory
 * so mount geometry and anchor metadata stay in one file. Returns an array of
 * `{ slotIndex, slotPosition, sensorPosition, sensorRotation }`, or empty when a model has none.
 */
export function getModelSlotDefs(modelType) {
  const key = normalizeModelKey(modelType || 'fallback');
  if (!slotDefsCachePromises.has(key)) {
    const pending = (async () => {
      let defs = [];
      switch (key) {
        case 'ac_induction_motor':
          defs = (await import('./acInductionMotorModel')).AC_INDUCTION_MOTOR_SLOT_DEFS || [];
          break;
        case 'dc_motor':
          defs = (await import('./dcMotorModel')).DC_MOTOR_SLOT_DEFS || [];
          break;
        case 'diesel_engine':
          defs = (await import('./dieselEngineModel')).DIESEL_ENGINE_SLOT_DEFS || [];
          break;
        case 'gas_turbine':
          defs = (await import('./gasTurbineModel')).GAS_TURBINE_SLOT_DEFS || [];
          break;
        case 'hydro_turbine':
          defs = (await import('./hydroTurbineModel')).HYDRO_TURBINE_SLOT_DEFS || [];
          break;
        case 'steam_turbine':
          defs = (await import('./steamTurbineModel')).STEAM_TURBINE_SLOT_DEFS || [];
          break;
        case 'wind_turbine':
          defs = (await import('./windTurbineModel')).WIND_TURBINE_SLOT_DEFS || [];
          break;
        case 'gearbox':
          defs = (await import('./gearboxModel')).GEARBOX_SLOT_DEFS || [];
          break;
          // A body absent from this switch is served no defs, gets no mount points, and
          // silently refuses every drop. registry.slotDefs.test.js guards against that.
        case 'mounted_bearing':
          defs = (await import('./mountedBearingModel')).MOUNTED_BEARING_SLOT_DEFS || [];
          break;
        case 'mill_bearing':
          defs = (await import('./millBearingModel')).MILL_BEARING_SLOT_DEFS || [];
          break;
        case 'chain_drive':
          defs = (await import('./chainDriveModel')).CHAIN_DRIVE_SLOT_DEFS || [];
          break;
        case 'pulley':
          defs = (await import('./pulleyModel')).PULLEY_SLOT_DEFS || [];
          break;
        case 'simple_shaft':
          defs = (await import('./simpleShaftModel')).SIMPLE_SHAFT_SLOT_DEFS || [];
          break;
        case 'flexible_coupling':
          defs = (await import('./flexibleCouplingModel')).FLEXIBLE_COUPLING_SLOT_DEFS || [];
          break;
        case 'jaw_coupling':
          defs = (await import('./jawCouplingModel')).JAW_COUPLING_SLOT_DEFS || [];
          break;
        case 'fluid_coupling':
          defs = (await import('./fluidCouplingModel')).FLUID_COUPLING_SLOT_DEFS || [];
          break;
        case 'magnetic_coupling':
          defs = (await import('./magneticCouplingModel')).MAGNETIC_COUPLING_SLOT_DEFS || [];
          break;
        case 'multi_stage_gearbox':
          defs = (await import('./multiStageGearboxModel')).MULTI_STAGE_GEARBOX_SLOT_DEFS || [];
          break;
        case 'multi_stage_gearbox_vertical':
          defs = (await import('./multiStageGearboxVerticalModel'))
            .MULTI_STAGE_GEARBOX_VERTICAL_SLOT_DEFS || [];
          break;
        case 'transformer':
          defs = (await import('./transformerModel')).TRANSFORMER_SLOT_DEFS || [];
          break;
        case 'circuit_breaker':
          defs = (await import('./circuitBreakerModel')).CIRCUIT_BREAKER_SLOT_DEFS || [];
          break;
        case 'electrical_panel':
          defs = (await import('./electricalPanelModel')).ELECTRICAL_PANEL_SLOT_DEFS || [];
          break;
        case 'switch_gear':
          defs = (await import('./switchGearModel')).SWITCH_GEAR_SLOT_DEFS || [];
          break;
        case 'surge_arrester':
          defs = (await import('./surgeArresterModel')).SURGE_ARRESTER_SLOT_DEFS || [];
          break;
        case 'substation':
          defs = (await import('./substationModel')).SUBSTATION_SLOT_DEFS || [];
          break;
        case 'vertical_cylinder_tank':
          defs = (await import('./verticalCylinderTankModel')).VERTICAL_CYLINDER_TANK_SLOT_DEFS || [];
          break;
        case 'vertical_capsule_tank':
          defs = (await import('./verticalCapsuleTankModel')).VERTICAL_CAPSULE_TANK_SLOT_DEFS || [];
          break;
        case 'cone_bottom_tank':
          defs = (await import('./coneBottomTankModel')).CONE_BOTTOM_TANK_SLOT_DEFS || [];
          break;
        case 'frustum_tank':
          defs = (await import('./frustumTankModel')).FRUSTUM_TANK_SLOT_DEFS || [];
          break;
        case 'horizontal_cylinder_tank':
          defs = (await import('./horizontalCylinderTankModel')).HORIZONTAL_CYLINDER_TANK_SLOT_DEFS || [];
          break;
        case 'horizontal_capsule_tank':
          defs = (await import('./horizontalCapsuleTankModel')).HORIZONTAL_CAPSULE_TANK_SLOT_DEFS || [];
          break;
        case 'horizontal_oval_tank':
          defs = (await import('./horizontalOvalTankModel')).HORIZONTAL_OVAL_TANK_SLOT_DEFS || [];
          break;
        case 'vertical_oval_tank':
          defs = (await import('./verticalOvalTankModel')).VERTICAL_OVAL_TANK_SLOT_DEFS || [];
          break;
        case 'rectangular_tank':
          defs = (await import('./rectangularTankModel')).RECTANGULAR_TANK_SLOT_DEFS || [];
          break;
        case 'gate_valve':
          defs = (await import('./gateValveModel')).GATE_VALVE_SLOT_DEFS || [];
          break;
        case 'globe_valve':
          defs = (await import('./globeValveModel')).GLOBE_VALVE_SLOT_DEFS || [];
          break;
        case 'diaphragm_valve':
          defs = (await import('./diaphragmValveModel')).DIAPHRAGM_VALVE_SLOT_DEFS || [];
          break;
        case 'disc_check_valve':
          defs = (await import('./discCheckValveModel')).DISC_CHECK_VALVE_SLOT_DEFS || [];
          break;
        case 'pinch_valve':
          defs = (await import('./pinchValveModel')).PINCH_VALVE_SLOT_DEFS || [];
          break;
        case 'plug_valve':
          defs = (await import('./plugValveModel')).PLUG_VALVE_SLOT_DEFS || [];
          break;
        case 'mechanical_float_trap':
          defs = (await import('./mechanicalFloatTrapModel'))
            .MECHANICAL_FLOAT_TRAP_SLOT_DEFS || [];
          break;
        case 'mechanical_inverted_bucket':
          defs = (await import('./mechanicalInvertedBucketModel'))
            .MECHANICAL_INVERTED_BUCKET_SLOT_DEFS || [];
          break;
        case 'steam_trap':
          defs = (await import('./steamTrapModel')).STEAM_TRAP_SLOT_DEFS || [];
          break;
        case 'thermodynamic_disc_trap':
          defs = (await import('./thermodynamicDiscTrapModel'))
            .THERMODYNAMIC_DISC_TRAP_SLOT_DEFS || [];
          break;
        case 'thermostatic_bellow_type':
          defs = (await import('./thermostaticBellowTypeModel'))
            .THERMOSTATIC_BELLOW_TYPE_SLOT_DEFS || [];
          break;
        case 'dehumidifier':
          defs = (await import('./dehumidifierModel')).DEHUMIDIFIER_SLOT_DEFS || [];
          break;
        case 'pac':
          defs = (await import('./pacModel')).PAC_SLOT_DEFS || [];
          break;
        case 'building_location':
          defs = (await import('./buildingLocationModel')).BUILDING_LOCATION_SLOT_DEFS || [];
          break;
        case 'generic':
          defs = (await import('./genericModel')).GENERIC_SLOT_DEFS || [];
          break;
        case 'pump':
          defs = (await import('./pumpModel')).PUMP_SLOT_DEFS || [];
          break;
        case 'axial_pump':
          defs = (await import('./axialPumpModel')).AXIAL_PUMP_SLOT_DEFS || [];
          break;
        case 'centrifugal_pump':
          defs = (await import('./centrifugalPumpModel')).CENTRIFUGAL_PUMP_SLOT_DEFS || [];
          break;
        case 'horizontal_single_shaft_centrifugal_pump':
          defs = (await import('./horizontalSingleShaftCentrifugalPumpModel'))
            .HORIZONTAL_SINGLE_SHAFT_CENTRIFUGAL_PUMP_SLOT_DEFS || [];
          break;
        case 'gear_pump':
          defs = (await import('./gearPumpModel')).GEAR_PUMP_SLOT_DEFS || [];
          break;
        case 'progressive_cavity_pump':
          defs = (await import('./progressiveCavityPumpModel'))
            .PROGRESSIVE_CAVITY_PUMP_SLOT_DEFS || [];
          break;
        case 'piston_pump':
          defs = (await import('./pistonPumpModel')).PISTON_PUMP_SLOT_DEFS || [];
          break;
        case 'single_shaft_multistage_pump':
          defs = (await import('./singleShaftMultistagePumpModel'))
            .SINGLE_SHAFT_MULTISTAGE_PUMP_SLOT_DEFS || [];
          break;
        case 'vertical_turbine_pump':
          defs = (await import('./verticalTurbinePumpModel'))
            .VERTICAL_TURBINE_PUMP_SLOT_DEFS || [];
          break;
        case 'screw_pump':
          defs = (await import('./screwPumpModel')).SCREW_PUMP_SLOT_DEFS || [];
          break;
        case 'coupling':
          defs = (await import('./couplingModel')).COUPLING_SLOT_DEFS || [];
          break;
        case 'solid_coupling':
          defs = (await import('./solidCouplingModel')).SOLID_COUPLING_SLOT_DEFS || [];
          break;
        case 'belt_drive':
          defs = (await import('./beltDriveModel')).BELT_DRIVE_SLOT_DEFS || [];
          break;
        case 'compressor':
          defs = (await import('./compressorModel')).COMPRESSOR_SLOT_DEFS || [];
          break;
        case 'centrifugal_fan':
          defs = (await import('./centrifugalFanModel')).CENTRIFUGAL_FAN_SLOT_DEFS || [];
          break;
        case 'axial_fan':
          defs = (await import('./axialFanModel')).AXIAL_FAN_SLOT_DEFS || [];
          break;
        case 'centrifugal_compressor':
          defs = (await import('./centrifugalCompressorModel'))
            .CENTRIFUGAL_COMPRESSOR_SLOT_DEFS || [];
          break;
        case 'axial_compressor':
          defs = (await import('./axialCompressorModel')).AXIAL_COMPRESSOR_SLOT_DEFS || [];
          break;
        case 'reciprocating_compressor':
          defs = (await import('./reciprocatingCompressorModel'))
            .RECIPROCATING_COMPRESSOR_SLOT_DEFS || [];
          break;
        case 'screw_compressor':
          defs = (await import('./screwCompressorModel')).SCREW_COMPRESSOR_SLOT_DEFS || [];
          break;
        case 'two_stage_screw_compressor':
          defs = (await import('./twoStageScrewCompressorModel'))
            .TWO_STAGE_SCREW_COMPRESSOR_SLOT_DEFS || [];
          break;
        case 'pillow_bearing':
          defs = (await import('./pillowBearingModel')).PILLOW_BEARING_SLOT_DEFS || [];
          break;
        case 'agitator':
          defs = (await import('./agitatorModel')).AGITATOR_SLOT_DEFS || [];
          break;
        case 'ball_mill':
          defs = (await import('./ballMillModel')).BALL_MILL_SLOT_DEFS || [];
          break;
        case 'sag_mill':
          defs = (await import('./sagMillModel')).SAG_MILL_SLOT_DEFS || [];
          break;
        case 'calendar_roll':
          defs = (await import('./calendarRollModel')).CALENDAR_ROLL_SLOT_DEFS || [];
          break;
        case 'jaw_crusher':
          defs = (await import('./jawCrusherModel')).JAW_CRUSHER_SLOT_DEFS || [];
          break;
        case 'conveyor':
          defs = (await import('./conveyorModel')).CONVEYOR_SLOT_DEFS || [];
          break;
        case 'conveyor_pulley':
          defs = (await import('./conveyorPulleyModel')).CONVEYOR_PULLEY_SLOT_DEFS || [];
          break;
        default:
          defs = [];
          break;
      }
      slotDefsCache.set(key, defs);
      return defs;
    })();
    // Evicted on failure for the same reason as getModel: a cached rejection would survive
    // every retry for the lifetime of the page.
    pending.catch(() => {
      slotDefsCachePromises.delete(key);
    });
    slotDefsCachePromises.set(key, pending);
  }
  return slotDefsCachePromises.get(key);
}
